
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PersonaAwareHeader, usePersonaBasePath } from '@/utils/headerUtils';
import { usePortfolio } from '@/hooks/usePortfolio';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { PortfolioHeader } from '@/components/portfolio/PortfolioHeader';
import { PortfolioTabs } from '@/components/portfolio/PortfolioTabs';
import { PortfolioType } from '@/types/portfolio';

const PortfolioDetail = () => {
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const { portfolio, isLoading: portfolioLoading, error: portfolioError } = usePortfolio(portfolioId || '');
  const [activeTab, setActiveTab] = useState('products');
  const basePath = usePersonaBasePath();

  // Set the initial active tab based on portfolio type
  useEffect(() => {
    if (portfolio) {
      if (portfolio.portfolio_type === 'buildings') {
        setActiveTab('buildings');
      } else if (portfolio.portfolio_type === 'formulated_products') {
        setActiveTab('products');
      } else {
        setActiveTab('products');
      }
    }
  }, [portfolio]);

  if (portfolioLoading) {
    return (
      <div className="min-h-screen bg-background">
        <PersonaAwareHeader />
        <main className="container py-12">
          <div className="flex justify-center items-center h-64">
            <p>Loading portfolio details...</p>
          </div>
        </main>
      </div>
    );
  }

  if (portfolioError) {
    return (
      <div className="min-h-screen bg-background">
        <PersonaAwareHeader />
        <main className="container py-12">
          <div className="bg-destructive/10 p-4 rounded-md text-destructive">
            An error occurred loading the portfolio: {(portfolioError as Error).message}
          </div>
          <div className="mt-6">
            <Link to={basePath}>
              <Button variant="outline" className="flex items-center gap-2">
                <ArrowRight className="h-4 w-4 rotate-180" />
                Back to Portfolios
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (!portfolio) {
    return (
      <div className="min-h-screen bg-background">
        <PersonaAwareHeader />
        <main className="container py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Portfolio Not Found</h1>
            <p className="text-muted-foreground mb-6">The portfolio you're looking for doesn't exist or you don't have access to view it.</p>
            <Link to="/client">
              <Button>Back to Portfolios</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PersonaAwareHeader />
      
      <main className="container py-12">
        <PortfolioHeader portfolio={portfolio} />
        
        <PortfolioTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          portfolioType={portfolio.portfolio_type as PortfolioType}
          portfolioId={portfolioId || ''}
        />
      </main>
    </div>
  );
};

export default PortfolioDetail;
